import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AnalyticsEvents, ISizesPartialComponent } from 'enums';
import { ROUTES } from 'config';
import { plugForDiscountImg } from 'static/images';
import { captureEvent } from 'utils/analytics/captureEvent';
import { translateSubCategory } from 'utils/translateSubcategories';
import { useAppSelector } from 'redux/hooks';
import { categoriesSelectors } from 'redux/modules/Categories';
import { useCurrentUser, useUserRoleId } from 'hooks';
import {
  CardBar,
  LabelMobile,
  FavoriteButton,
  RatingBadge,
} from 'view/molecules/SaleCardComponents';
import { ShortDiscountContent } from 'interfaces';
import * as S from './SaleCard.styles';

type Props = {
  discount: ShortDiscountContent;
  isFavorite: boolean;
  isNew?: boolean;
  isExpired?: boolean;
  isCheckRelevance?: boolean;
  cardBarBgColor?: string;
  saleCardWidth?: ISizesPartialComponent;
  categoryTitle?: string;
};

export const SaleCard = ({
  discount,
  isFavorite,
  isNew,
  isExpired,
  isCheckRelevance,
  cardBarBgColor,
  saleCardWidth,
  categoryTitle,
}: Props) => {
  const { i18n } = useTranslation();
  const user = useCurrentUser();
  const roleId = useUserRoleId();
  const categories = useAppSelector(categoriesSelectors.getCategories);
  const [imageSrc, setImageSrc] = useState(plugForDiscountImg);

  const { subcategory } = discount;
  // The `categoryTitle` prop is not passed by every caller, so resolve the
  // owning category from the subcategory itself and fall back to the prop.
  const subcategoryCategoryTitle =
    categories?.find((category) => category.id === subcategory?.categoryId)
      ?.title ?? categoryTitle;

  const subcategoryTitle = subcategory
    ? translateSubCategory(
        subcategory.title,
        subcategoryCategoryTitle ?? '',
        i18n.language,
      )
    : discount.type;

  useEffect(() => {
    if (!discount.image) {
      setImageSrc(plugForDiscountImg);
      return;
    }
    const img = new Image();
    img.onload = () => setImageSrc(discount.image);
    img.onerror = () => setImageSrc(plugForDiscountImg);
    img.src = discount.image;
  }, [discount.image]);

  const partnerClickHandler = () => {
    if (user?.id && roleId) {
      captureEvent(AnalyticsEvents.SelectCard, {
        user_id: String(user.id),
        item_id: String(discount.id),
        item_name: discount.companyName,
        item_category: categoryTitle ?? '',
        event_time: new Date().toISOString(),
      });
    }
  };

  return (
    <S.SaleCard
      to={`${ROUTES.discountRoot}/${discount.id}`}
      salecardwidth={saleCardWidth}
      onClick={partnerClickHandler}
    >
      {isNew && isCheckRelevance && !isExpired ? (
        <S.StickersDiv>
          <LabelMobile kind='small2' isNew={isNew} isOnDiscountPage />
          <LabelMobile
            kind='small2'
            isCheckRelevance={isCheckRelevance}
            isOnDiscountPage
          />
        </S.StickersDiv>
      ) : (
        <>
          {isNew && <LabelMobile kind='small2' isNew={isNew} />}
          {isExpired && <LabelMobile kind='small2' isExpired={isExpired} />}
          {isCheckRelevance && !isExpired && (
            <LabelMobile kind='small2' isCheckRelevance={isCheckRelevance} />
          )}
        </>
      )}

      <S.FavoriteContainer>
        <FavoriteButton
          discountId={discount.id}
          isFavorite={isFavorite}
          discountTitle={discount.companyName}
          itemCategory={categoryTitle ?? ''}
        />
      </S.FavoriteContainer>

      <S.RatingContainer>
        <RatingBadge kind='small2' averageRating={discount.averageRating} />
      </S.RatingContainer>

      <S.SaleCardImg
        isExpired={isExpired}
        alt='discount cover photo'
        src={imageSrc}
      />

      <CardBar
        title={discount.companyName}
        type={subcategoryTitle}
        sizeMin={discount.sizeMin}
        sizeMax={discount.sizeMax}
        cardBarBgColor={cardBarBgColor}
      />
    </S.SaleCard>
  );
};
